<template>
	<div class="initial-chart-section">
		<AppCard :title="getString('today')">
			<template v-slot:body>
				<div class="row initial-chart-container">
					<div class="col-9 initial-chart-col">
						<AppChart
							type="area"
							:series="ordersSeries"
							:xaxis="xaxisValues"
							:xaxis-annotations="xaxisAnnotations"
							:gradient-colors="chartColors"
							:colors="['#dadada', '#6c33cb']"
							height="100%"
							full-width
							:badge-live="showLiveBadge"
							:formatter="r => readableEurValue(r || 0, 0)"
							class="initial-chart"
							:dark="userStore.darkMode"
							:force-y-axis-zero="true"
						/>
					</div>
					<div class="col-3 initial-side-info">
						<UserInitialCardInfo class="initial-side-info-square" blue :title="getString('gross_volume')" :value="dividedSeries.ordersTotal" :last-period-value="dividedSeries.previousOrdersTotal" />
						<UserInitialCardInfo class="initial-side-info-square" blue :title="getString('orders_volume')" no-currency :value="dividedSeries.ordersNum" :last-period-value="dividedSeries.previousOrdersNum" />
						<UserInitialCardInfo class="initial-side-info-square" blue :title="getString('average_receipt')" with-cents :value="dividedSeries.averageBill" :last-period-value="dividedSeries.previousAverageBill" />
						<UserInitialCardInfo class="initial-side-info-square" blue :title="getString('covers')" no-currency :value="dividedSeries.covers" :last-period-value="dividedSeries.previousCovers" />
					</div>
				</div>
			</template>
		</AppCard>
	</div>
</template>

<script>
import UserInitialCardInfo from "@/user/components/analytics/UserInitialCardInfo.vue";

import AppChart from "@/shared/components/AppChart.vue";
import AppCard from "@/shared/components/AppCard.vue";

import {useLanguageStore} from "@/shared/stores";
import {useUserStore} from "@/user/stores";

export default {
	setup() {
		const languageStore = useLanguageStore();
		return {getString: languageStore.getString, userStore: useUserStore(), readableEurValue: languageStore.readableEurValue, languageStore};
	},
	name: "UserInitialCard",
	components: { AppChart, AppCard, UserInitialCardInfo },
	data() {
		return {
			timeSpans: [],
		};
	},
	props: {
		aggregatedData: {
			type: Object,
			default: null
		},
		previousAggregatedData: {
			type: Object,
			default: null
		},
		showLiveBadge: {
			type: Boolean,
			default: false
		}
	},
	computed: {
		chartColors() {
			return this.userStore.darkMode ? [null, '#0D71DE'] : [null, '#0752A3'];
		},
		xaxisAnnotations() {
			let annotations = [];
			if(this.dayShift) {
				annotations = [
					{
						x: this.localeString(this.getLastShiftDatetime()),
						borderColor: 'rgba(108,51,203,0.7)',
						strokeDashArray: 3,
						label: {
							borderColor: 'transparent',
							orientation: 'horizontal',
							style: {
								background: 'transparent',
								color: 'transparent',
								padding: {
									left: 0,
									right: 0,
									top: 0,
									bottom: 0,
								}
							},
						}
					}
				];
			}
			return annotations;
		},
		dayShift() {
			return this.userStore && this.userStore.currBusiness && this.userStore.currBusiness.day_shift ? this.userStore.currBusiness.day_shift : '03:00:00';
		},
		xaxisValues() {
			if (!this.timeSpans || this.timeSpans.length === 0) {
				this.timeSpans = this.getHourlyTimeSpansForCurrentDay();
			}
			return this.timeSpans.map(timeSpan => this.localeString(timeSpan));
		},
		ordersSeries() {
			if (!this.timeSpans || this.timeSpans.length === 0) {
				this.timeSpans = this.getHourlyTimeSpansForCurrentDay();
			}
			return [
				{name: this.getString('previous_period'), data: this.dividedSeries.previousSeries},
				{name: this.getString('current_period'), data: this.dividedSeries.series},
			];
		},
		dividedSeries() {
			let result = {
				series: [],
				previousSeries: [],
				ordersNum: 0,
				previousOrdersNum: 0,
				ordersTotal: 0,
				previousOrdersTotal: 0,
				ordersNet: 0,
				previousOrdersNet: 0,
				averageBill: 0,
				previousAverageBill: 0,
				covers: 0,
				previousCovers: 0,
			};
			
			if (!this.timeSpans || this.timeSpans.length === 0) {
				this.timeSpans = this.getHourlyTimeSpansForCurrentDay();
			}
			
			let finalSpans = this.timeSpans.map(time => 0);
			let dayShift = this.getLastShiftDatetime();
			
			// Process aggregated data
			if (this.aggregatedData && this.aggregatedData.full && this.aggregatedData.full.length > 0) {
				for(let i = 0; i < this.aggregatedData.full.length; i++) {
					let row = this.aggregatedData.full[i];
					let parsedTime = new Date(row.hour_bucket || row.day_bucket);
					let timeIndex = -1;
					
					for(let j = 0; j < this.timeSpans.length; j++) {
						if(parsedTime.getHours() === this.timeSpans[j].getHours() && 
						   parsedTime.getDate() === this.timeSpans[j].getDate()) {
							timeIndex = j;
							break;
						}
					}
					
					if(timeIndex >= 0 && timeIndex < finalSpans.length) {
						const grossSales = row.gross_sales / 100;
						finalSpans[timeIndex] += grossSales;
						result.ordersTotal += grossSales;
						result.ordersNet += grossSales; // No fee data in aggregated view
						result.ordersNum += row.orders_cnt;
						result.covers += row.cover_ctn || 0;
					}
				}
			}
			result.averageBill = result.ordersNum ? result.ordersTotal / result.ordersNum : 0;
			result.series = finalSpans;

			let finalSpansPrevious = this.timeSpans.map(time => 0);
			
			// Process previous period aggregated data
			if (this.previousAggregatedData && this.previousAggregatedData.full && this.previousAggregatedData.full.length > 0) {
				for(let i = 0; i < this.previousAggregatedData.full.length; i++) {
					let row = this.previousAggregatedData.full[i];
					let parsedTime = new Date(row.hour_bucket || row.day_bucket);
					parsedTime.setDate(parsedTime.getDate() + 1);
					
					let timeIndex = -1;
					for(let j = 0; j < this.timeSpans.length; j++) {
						if(parsedTime.getHours() === this.timeSpans[j].getHours() && 
						   parsedTime.getDate() === this.timeSpans[j].getDate()) {
							timeIndex = j;
							break;
						}
					}
					
					if(timeIndex >= 0 && timeIndex < finalSpansPrevious.length) {
						const grossSales = row.gross_sales / 100;
						finalSpansPrevious[timeIndex] += grossSales;
						result.previousOrdersTotal += grossSales;
						result.previousOrdersNet += grossSales; // No fee data in aggregated view
						result.previousOrdersNum += row.orders_cnt;
						result.previousCovers += row.cover_ctn || 0;
					}
				}
			}
			result.previousAverageBill = result.previousOrdersNum ? result.previousOrdersTotal / result.previousOrdersNum : 0;
			result.previousSeries = finalSpansPrevious;

			// Make series cumulative
			for(let i = 1; i < finalSpans.length; i++) {
				finalSpans[i] += finalSpans[i-1];
			}
			for(let i = 1; i < finalSpansPrevious.length; i++) {
				finalSpansPrevious[i] += finalSpansPrevious[i-1];
			}

			return result;
		},
	},
	methods: {
		localeString(date) {
			date = date ? new Date(date) : new Date();
			let options = {hour: '2-digit', minute:'2-digit', hour12: false};
			return date.toLocaleTimeString([this.languageStore.lang || 'en-US'], options);
		},
		getHourlyTimeSpansForCurrentDay() {
			let dayShift = this.getLastShiftDatetime();
			let now = new Date();

			let endDate = new Date(now);
			if (endDate.getMinutes() > 0 || endDate.getSeconds() > 0) {
				endDate.setTime(endDate.getTime() + 3600 * 1000);
			}
			endDate.setMinutes(0, 0, 0);

			// Start from today's day shift time instead of 24 hours ago
			let startDate = new Date(dayShift);
			
			let dates = [];
			let iterDate = new Date(startDate);
			
			while (iterDate <= endDate) {
				dates.push(new Date(iterDate));
				iterDate.setTime(iterDate.getTime() + 3600 * 1000);
			}
			
			if (dayShift) {
				for (let i = 0; i < dates.length - 1; i++) {
					if (dates[i] <= dayShift && dates[i + 1] > dayShift) {
						if (dayShift.getMinutes() !== 0) {
							dates.splice(i + 1, 0, dayShift);
						}
						break;
					}
				}
			}
			
			return dates;
		},
		getLastShiftDatetime() {
			let splitShift = this.dayShift.split(':');
			let current = new Date();
			let dayShift = new Date();
			dayShift.setHours(splitShift[0] || 0, splitShift[1] || 0, splitShift[2] || 0, 0);
			if(dayShift > current) {
				dayShift.setDate(dayShift.getDate() - 1);
			}
			return dayShift;
		}
	},
	created() {
		this.timeSpans = this.getHourlyTimeSpansForCurrentDay();
	}
}
</script>

<style lang="scss" scoped>
.initial-chart-section {
	max-width: 1400px;
	margin: 0 auto;

	> .card-container {
		min-width: 100%;

		.initial-chart-container {
			align-items: stretch;
		}
		.chart-container {
			&:deep(.card-container) {
				box-shadow: none;

				.card-body {
					padding-top: 0;
					padding-bottom: 0;
				}
			}
			&:deep(.chart-svg-container) {
				height: 300px;
				padding-left: 10px;
			}
		}
		&:deep(.card-body) {
			padding-left: 0;
			padding-bottom: 0;
			padding-top: 0;

			.initial-chart-col {
				justify-content: center;
				width: 75%;
				max-width: calc(100% - 250px);
				min-width: calc(100% - 300px);
			}
			.initial-side-info {
				min-width: 250px;
				max-width: 300px;
				padding-bottom: 12px;
				padding-top: 12px;

				.initial-side-info-square {
					&:not(:last-child) {
						margin-bottom: 12px;
					}
				}
			}

			@media (max-width: 1080px) {
				.chart-container {
					padding-top: 8px;
				}
				.initial-chart-col {
					width: 100%;
					max-width: 100%;
					min-width: 100%;
				}
				.initial-side-info {
					flex-direction: row;
					justify-content: space-between;
					align-items: flex-start;
					min-width: 100%;
					max-width: 100%;
					padding-bottom: 0;

					.initial-side-info-square {
						height: unset;
						margin-left: 12px;
						margin-bottom: 12px;
					}
				}
			}

			@media (max-width: 960px) {
				.initial-side-info {
					flex-wrap: wrap;
					padding-top: 0;

					.initial-side-info-square {
						width: calc(50% - 12px);
						margin-left: 12px;
						margin-bottom: 12px;
					}
				}
			}

			@media (max-width: 768px) {
				.initial-side-info {

					.initial-side-info-square {
						width: calc(50% - 8px);
						margin-left: 8px;
						margin-bottom: 8px;
						&:not(:last-child) {
							margin-bottom: 8px;
						}
					}
				}
			}

			@media (max-width: 460px) {
				.initial-side-info {
					justify-content: center;

					.initial-side-info-square {
						width: calc(100% - 8px);
					}
				}
			}

		}
	}
}
</style>